(() => {
  /* ---------- Simple scroll-snap slider ---------- */
  if (!customElements.get('pb-slider')) {
    customElements.define(
      'pb-slider',
      class PbSlider extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-track]');
          if (!this.track) return;
          this.slides = Array.from(this.track.querySelectorAll('[data-slide]'));
          this.index = 0;
          this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
          this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
          this.dots = this.querySelector('[data-dots]');
          if (this.dots) {
            this.slides.forEach((_, i) => {
              const b = document.createElement('button');
              b.type = 'button';
              b.setAttribute('aria-label', `${i + 1}`);
              b.addEventListener('click', () => this.go(i));
              this.dots.appendChild(b);
            });
          }
          this.track.addEventListener('scroll', () => {
            window.clearTimeout(this._t);
            this._t = window.setTimeout(() => this.sync(), 80);
          });
          this.sync();
          if (this.dataset.autoplay === 'true' && this.slides.length > 1) {
            const speed = parseInt(this.dataset.speed, 10) || 5000;
            const start = () => (this._timer = window.setInterval(() => this.go(this.index + 1), speed));
            const stop = () => window.clearInterval(this._timer);
            this.addEventListener('mouseenter', stop);
            this.addEventListener('mouseleave', start);
            this.addEventListener('touchstart', stop, { passive: true });
            start();
          }
        }
        go(i) {
          const n = this.slides.length;
          if (!n) return;
          this.index = ((i % n) + n) % n;
          this.track.scrollTo({ left: this.slides[this.index].offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
          this.paint();
        }
        sync() {
          const left = this.track.scrollLeft;
          let best = 0;
          let dist = Infinity;
          this.slides.forEach((s, i) => {
            const d = Math.abs(s.offsetLeft - this.track.offsetLeft - left);
            if (d < dist) {
              dist = d;
              best = i;
            }
          });
          this.index = best;
          this.paint();
        }
        paint() {
          if (!this.dots) return;
          Array.from(this.dots.children).forEach((d, i) => d.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
        }
      }
    );
  }

  /* ---------- Delivery estimate dates ---------- */
  const formatDate = (date, format, days, months) => {
    const d = date.getDate();
    const m = date.getMonth();
    const wd = days[(date.getDay() + 6) % 7] || '';
    const mon = months[m] || '';
    const dd = String(d).padStart(2, '0');
    const mm = String(m + 1).padStart(2, '0');
    switch (format) {
      case 'day_mm_dd':
        return `${wd}, ${mon} ${d}`;
      case 'day_dd_mm':
        return `${wd}, ${d}. ${mon}`;
      case 'dd_mm':
        return `${d}. ${mon}`;
      case 'dd_mm_no_dot':
        return `${d} ${mon}`;
      case 'day_dd_mm_numeric':
        return `${wd}, ${dd}.${mm}`;
      case 'dd_mm_numeric':
        return `${dd}.${mm}`;
      default:
        return `${mon} ${d}`;
    }
  };
  const initCheckpoints = (root = document) => {
    root.querySelectorAll('[data-checkpoints]').forEach((el) => {
      const format = el.dataset.format;
      const days = (el.dataset.days || '').split(',').map((s) => s.trim());
      const months = (el.dataset.months || '').split(',').map((s) => s.trim());
      el.querySelectorAll('[data-template]').forEach((node) => {
        const add = (n) => {
          const dt = new Date();
          dt.setDate(dt.getDate() + (parseInt(n, 10) || 0));
          return formatDate(dt, format, days, months);
        };
        node.innerHTML = node.dataset.template
          .replace(/\[start_date\]/g, add(node.dataset.min))
          .replace(/\[end_date\]/g, add(node.dataset.max));
      });
    });
  };

  /* ---------- Money formatting ---------- */
  const formatMoney = (cents, format) => {
    const value = typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : cents;
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(decimals);
      const [int, dec] = fixed.split('.');
      const intStr = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? `${intStr}${decimal}${dec}` : intStr;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return fmt(value, 0);
        case 'amount_with_comma_separator':
          return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return fmt(value, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return fmt(value, 0, ' ', ',');
        case 'amount_with_space_separator':
          return fmt(value, 2, ' ', ',');
        default:
          return fmt(value, 2);
      }
    });
  };

  /* ---------- Sticky add to cart ---------- */
  if (!customElements.get('sticky-atc')) {
    customElements.define(
      'sticky-atc',
      class StickyAtc extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('[data-sticky-button]');
          this.select = this.querySelector('[data-variant-select]');
          this.button?.addEventListener('click', this.onClick.bind(this));
          this.select?.addEventListener('change', this.onSelect.bind(this));

          if (this.dataset.mode !== 'always') {
            const anchor =
              document.getElementById(`ProductSubmitButton-${this.dataset.section}`) ||
              document.getElementById(`ProductInfo-${this.dataset.section}`);
            if (anchor && 'IntersectionObserver' in window) {
              new IntersectionObserver(([entry]) => {
                const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
                this.classList.toggle('is-visible', passed);
              }).observe(anchor);
            }
          }

          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
              if (event.data.sectionId !== this.dataset.section) return;
              this.update(event.data.variant);
            });
          }
        }
        disconnectedCallback() {
          this.unsub?.();
        }
        update(variant) {
          if (!variant) return;
          const price = this.querySelector('[data-price]');
          if (price) {
            const fmt = this.dataset.moneyFormat || '{{amount}}';
            price.querySelector('.sticky-atc__price-current').textContent = formatMoney(variant.price, fmt);
            let compare = price.querySelector('.sticky-atc__price-compare');
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
              if (!compare) {
                compare = document.createElement('s');
                compare.className = 'sticky-atc__price-compare';
                price.appendChild(compare);
              }
              compare.textContent = formatMoney(variant.compare_at_price, fmt);
            } else if (compare) {
              compare.remove();
            }
          }
          if (this.select) this.select.value = String(variant.id);
          if (!this.dataset.scrollTarget && this.button) {
            this.button.disabled = !variant.available;
            const label = this.button.querySelector('[data-label]');
            if (label) {
              if (!this._label) this._label = label.textContent;
              label.textContent = variant.available ? this._label : window.variantStrings?.soldOut || label.textContent;
            }
          }
        }
        onSelect() {
          const option = this.select.selectedOptions[0];
          if (!option) return;
          const values = (option.dataset.options || '').split('||');
          const pickers = document.querySelector(`#variant-selects-${this.dataset.section}`);
          if (!pickers) return;
          const groups = pickers.querySelectorAll('fieldset, .product-form__input--dropdown');
          values.forEach((value, i) => {
            const group = groups[i];
            if (!group) return;
            const select = group.querySelector('select');
            if (select) {
              const opt = Array.from(select.options).find((o) => o.value === value);
              if (opt && !opt.selected) {
                select.value = value;
                select.dispatchEvent(new Event('change', { bubbles: true }));
              }
              return;
            }
            const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find((r) => r.value === value);
            if (radio && !radio.checked) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }
        onClick() {
          const target = this.dataset.scrollTarget;
          if (target) {
            const el = document.querySelector(target);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
          }
          const submit = document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
          if (submit && !submit.disabled) {
            submit.click();
          } else {
            const form = document.getElementById(this.dataset.form);
            form?.requestSubmit ? form.requestSubmit() : form?.submit();
          }
        }
      }
    );
  }

  /* ---------- Cart reservation timer ---------- */
  if (!customElements.get('cart-timer')) {
    customElements.define(
      'cart-timer',
      class CartTimer extends HTMLElement {
        connectedCallback() {
          const key = 'cart-timer-end';
          const duration = (parseInt(this.dataset.duration, 10) || 300) * 1000;
          let end = 0;
          try {
            end = parseInt(sessionStorage.getItem(key), 10) || 0;
          } catch (e) {}
          if (!end || end < Date.now()) {
            end = Date.now() + duration;
            try {
              sessionStorage.setItem(key, String(end));
            } catch (e) {}
          }
          this.end = end;
          this.value = this.querySelector('[data-timer-value]');
          this.tick();
          this._i = window.setInterval(() => this.tick(), 1000);
        }
        disconnectedCallback() {
          window.clearInterval(this._i);
        }
        tick() {
          if (!this.value) return;
          const left = Math.max(0, Math.round((this.end - Date.now()) / 1000));
          const m = String(Math.floor(left / 60)).padStart(2, '0');
          const s = String(left % 60).padStart(2, '0');
          this.value.textContent = `${m}:${s}`;
        }
      }
    );
  }

  /* ---------- Free shipping bar: currency conversion ---------- */
  const fixProgress = (root = document) => {
    const rate = parseFloat(window.Shopify?.currency?.rate || '1');
    if (!rate || rate === 1) return;
    root.querySelectorAll('.cart-extras__progress:not([data-fixed])').forEach((el) => {
      el.dataset.fixed = 'true';
      const goal = Math.round(parseInt(el.dataset.goal, 10) * rate);
      const total = parseInt(el.dataset.total, 10) || 0;
      const remaining = goal - total;
      const pct = Math.min(100, (total / goal) * 100);
      const text = el.querySelector('[data-progress-text]');
      const fill = el.querySelector('[data-progress-fill]');
      const icon = el.querySelector('[data-progress-icon]');
      if (text) {
        text.innerHTML =
          remaining > 0
            ? el.dataset.message.replace('[amount]', formatMoney(remaining, el.dataset.moneyFormat || '{{amount}}'))
            : el.dataset.success;
      }
      if (fill) fill.style.width = `${pct}%`;
      if (icon) icon.style.left = `${pct}%`;
    });
  };
  if (parseFloat(window.Shopify?.currency?.rate || '1') !== 1) {
    new MutationObserver(() => fixProgress()).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ---------- Back to top ---------- */
  const initBackToTop = () => {
    const btn = document.querySelector('[data-back-to-top]');
    if (!btn) return;
    const after = parseInt(btn.dataset.after, 10) || 400;
    const onScroll = () => btn.classList.toggle('is-visible', window.scrollY > after);
    window.addEventListener('scroll', onScroll, { passive: true });
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
    onScroll();
  };

  /* ---------- Content protection (optional theme setting) ---------- */
  const initProtection = () => {
    if (document.documentElement.dataset.protect !== 'true' || window.Shopify?.designMode) return;
    const block = (e) => {
      const t = e.target;
      if (t && (t.closest('input, textarea, [contenteditable="true"]'))) return;
      e.preventDefault();
    };
    document.addEventListener('contextmenu', block);
    document.addEventListener('copy', block);
    document.addEventListener('dragstart', (e) => {
      if (e.target && e.target.tagName === 'IMG') e.preventDefault();
    });
    document.addEventListener('keydown', (e) => {
      const k = (e.key || '').toLowerCase();
      if (k === 'f12' || ((e.ctrlKey || e.metaKey) && e.shiftKey && ['i', 'j', 'c'].includes(k)) || ((e.ctrlKey || e.metaKey) && k === 'u')) {
        e.preventDefault();
      }
    });
  };

  /* ---------- Open external links in a new tab ---------- */
  const initExternalLinks = () => {
    Array.from(document.links).forEach((link) => {
      if (link.hostname && link.hostname !== window.location.hostname) {
        link.target = '_blank';
        link.rel = 'noreferrer noopener';
      }
    });
  };

  /* ---------- Section anchor links that survive theme changes ---------- */
  const findSectionTarget = (hash) => {
    if (!hash || hash.indexOf('#shopify-section-') !== 0) return null;
    const id = decodeURIComponent(hash.slice(1));
    const exact = document.getElementById(id);
    if (exact) return exact;
    const key = id.split('__').pop();
    return key ? document.querySelector(`[id$="__${CSS.escape(key)}"]`) : null;
  };
  document.addEventListener('click', (e) => {
    const link = e.target.closest && e.target.closest('a[href*="#shopify-section-"]');
    if (!link) return;
    const url = new URL(link.href, window.location.href);
    if (url.pathname !== window.location.pathname) return;
    const target = findSectionTarget(url.hash);
    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  const init = () => {
    const hashTarget = findSectionTarget(window.location.hash);
    if (hashTarget) window.setTimeout(() => hashTarget.scrollIntoView({ block: 'start' }), 300);
    initExternalLinks();
    fixProgress();
    initCheckpoints();
    initBackToTop();
    initProtection();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', (e) => initCheckpoints(e.target));
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => window.setTimeout(() => initCheckpoints(), 50));
  }
})();
